import { ShoppingCart, Clock, AlertTriangle, CheckCircle2, TrendingUp, TrendingDown } from 'lucide-react';
import { formatDuration } from '../lib/utils';
import type { KPIData } from '../types';

interface KPICardsProps {
  data: KPIData;
  previousPeriod?: KPIData;
}

function KPICard({
  title,
  value,
  previousValue,
  icon: Icon,
  iconBg,
  formatter,
}: {
  title: string;
  value: number;
  previousValue?: number;
  icon: React.ElementType;
  iconBg: string;
  formatter: (v: number) => string;
}) {
  const change = previousValue !== undefined && previousValue !== 0
    ? ((value - previousValue) / previousValue) * 100
    : null;

  return (
    <div className="oem-card p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-oem-muted">{title}</p>
          <p className="mt-2 font-display text-4xl text-oem-navy tracking-tight">
            {formatter(value)}
          </p>
          {change !== null && (
            <div className="mt-2 flex items-center gap-1 text-sm font-medium">
              {change >= 0 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-600">{Math.abs(change).toFixed(1)}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">{Math.abs(change).toFixed(1)}%</span>
                </>
              )}
              <span className="text-oem-muted font-normal ml-1">vs last period</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl shrink-0 ${iconBg}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

export function KPICards({ data, previousPeriod }: KPICardsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KPICard
        title="Total Transactions"
        value={data.totalTransactions}
        previousValue={previousPeriod?.totalTransactions}
        icon={ShoppingCart}
        iconBg="bg-oem-800"
        formatter={(v) => v.toLocaleString()}
      />
      <KPICard
        title="Avg. Time to Close"
        value={data.avgTimeToClose}
        previousValue={previousPeriod?.avgTimeToClose}
        icon={Clock}
        iconBg="bg-accent-500"
        formatter={formatDuration}
      />
      <KPICard
        title="Abandoned Carts"
        value={data.abandonedCarts}
        previousValue={previousPeriod?.abandonedCarts}
        icon={AlertTriangle}
        iconBg="bg-violet-500"
        formatter={(v) => v.toLocaleString()}
      />
      <KPICard
        title="Automation Rate"
        value={data.totalTransactions > 0 ? (data.statusBreakdown.automation / data.totalTransactions) * 100 : 0}
        previousValue={previousPeriod && previousPeriod.totalTransactions > 0
          ? (previousPeriod.statusBreakdown.automation / previousPeriod.totalTransactions) * 100
          : undefined}
        icon={CheckCircle2}
        iconBg="bg-emerald-500"
        formatter={(v) => `${v.toFixed(1)}%`}
      />
    </div>
  );
}
